import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  UtensilsCrossed,
  Sparkles,
  Star,
  Wifi,
  Clock,
  Globe,
  ArrowRight,
  Check,
} from 'lucide-react';
import { useQrLanguage, QR_LANGUAGES, QrLanguage } from './LanguageContext';

interface QRMenuWelcomeProps {
  masaName?: string;
  restaurantName?: string;
  logo?: string;
  wifiName?: string;
  workingHours?: string;
  onStart: () => void;
  onLanguageChange?: (lang: QrLanguage) => void;
}

const BACKGROUND_IMAGE =
  'https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=1080&auto=format&fit=crop';

export default function QRMenuWelcome({
  masaName,
  restaurantName,
  logo,
  wifiName = 'rezmo2026',
  workingHours = '09:00 - 23:00',
  onStart,
  onLanguageChange,
}: QRMenuWelcomeProps) {
  const { language, setLanguage, t } = useQrLanguage();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLangSelect = (lang: QrLanguage) => {
    setLanguage(lang);
    setIsLangOpen(false);
    onLanguageChange?.(lang);
    // Light haptic on language change for native feel
    try {
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        (navigator as any).vibrate(8);
      }
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="fixed inset-0 bg-black overflow-hidden qrm-no-select">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src={BACKGROUND_IMAGE}
          alt=""
          className="w-full h-full object-cover"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-[#0a0a0a]" />
      </div>

      {/* Floating orbs */}
      <motion.div
        className="absolute top-10 -left-10 w-72 h-72 rounded-full bg-orange-500/30 blur-3xl"
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-32 -right-10 w-72 h-72 rounded-full bg-amber-500/25 blur-3xl"
        animate={{ scale: [1.15, 1, 1.15], opacity: [0.5, 0.3, 0.5] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 px-5 pt-6 qrm-safe-top flex items-center justify-between">
        {/* Masa badge */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="flex items-center gap-2 bg-white/10 backdrop-blur-xl border border-white/20 px-4 py-2 rounded-full shadow-lg"
        >
          <span className="relative flex w-2 h-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
          </span>
          <span className="text-white text-[12px] font-bold tracking-wider">
            {t.table} {masaName || '-'}
          </span>
        </motion.div>

        {/* Language switcher */}
        <motion.div
          ref={langRef}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="relative"
        >
          <button
            type="button"
            onClick={() => setIsLangOpen((o) => !o)}
            className="flex items-center gap-1.5 bg-white/10 backdrop-blur-xl border border-white/20 px-3 py-2 rounded-full shadow-lg hover:bg-white/20 transition"
          >
            <Globe className="w-3.5 h-3.5 text-white" />
            <span className="text-white text-[12px] font-bold tracking-wider">
              {language.toUpperCase()}
            </span>
          </button>
          <AnimatePresence>
            {isLangOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 420, damping: 28 }}
                className="absolute top-full right-0 mt-2 w-48 bg-[#1c1c1e]/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
              >
                {QR_LANGUAGES.map((lang) => {
                  const isActive = lang.code === language;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => handleLangSelect(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors active:scale-[0.98] ${
                        isActive
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'text-white hover:bg-white/5'
                      }`}
                    >
                      <span className="text-[11px] font-black tracking-wider opacity-80 w-7">
                        {lang.flag}
                      </span>
                      <span className="text-[14px] font-semibold flex-1">{lang.name}</span>
                      {isActive && (
                        <Check className="w-4 h-4 text-orange-400" strokeWidth={3} />
                      )}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Hero Content */}
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-8 pt-24 pb-72">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6, type: 'spring', bounce: 0.3 }}
          className="w-24 h-24 rounded-3xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-2xl shadow-orange-500/40 mb-8"
        >
          {logo ? (
            <img
              src={logo}
              alt={restaurantName || 'logo'}
              className="w-14 h-14 rounded-2xl object-cover"
            />
          ) : (
            <UtensilsCrossed className="w-12 h-12 text-white" strokeWidth={2.5} />
          )}
        </motion.div>

        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-[48px] leading-tight font-extrabold text-white tracking-tight text-center mb-4"
        >
          {t.welcome}
        </motion.h1>

        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="text-white/70 text-[15px] font-medium text-center max-w-[280px] leading-relaxed"
        >
          {t.welcomeDescription}
        </motion.p>

        {restaurantName ? (
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.65, duration: 0.6 }}
            className="text-white/40 text-[12px] font-bold uppercase tracking-[0.2em] mt-3"
          >
            {restaurantName}
          </motion.p>
        ) : null}
      </div>

      {/* Bottom Card with Features + CTA */}
      <div className="absolute bottom-0 left-0 right-0 z-20 px-5 pb-8 qrm-safe-bottom">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="grid grid-cols-2 gap-3 mb-4"
        >
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-4">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center mb-3">
              <Sparkles className="w-5 h-5 text-orange-400" />
            </div>
            <h3 className="text-white font-bold text-[14px] mb-1">{t.quickOrder}</h3>
            <p className="text-white/50 text-[11px] leading-tight">{t.quickOrderDesc}</p>
          </div>
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center mb-3">
              <Star className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="text-white font-bold text-[14px] mb-1">{t.specialFlavors}</h3>
            <p className="text-white/50 text-[11px] leading-tight">{t.specialFlavorsDesc}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="flex items-center justify-center gap-4 mb-4 text-white/70"
        >
          <div className="flex items-center gap-1.5">
            <Wifi className="w-3.5 h-3.5" />
            <span className="text-[13px] font-bold tracking-wide">{wifiName}</span>
          </div>
          <span className="w-px h-4 bg-white/20" />
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            <span className="text-[13px] font-bold tracking-wide">{workingHours}</span>
          </div>
        </motion.div>

        <motion.button
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          type="button"
          onClick={onStart}
          className="relative w-full h-16 rounded-3xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-[17px] hover:scale-[1.02] active:scale-[0.98] transition-transform flex items-center justify-center gap-2 overflow-hidden group"
        >
          <span className="relative">{t.viewMenu}</span>
          <ArrowRight className="w-5 h-5 relative group-hover:translate-x-1 transition-transform" />
        </motion.button>
      </div>
    </div>
  );
}
